import axios from "axios";

const rawUrl = process.env.REACT_APP_DIRECTUS_URL;
if (rawUrl == null || !String(rawUrl).trim()) {
  throw new Error("REACT_APP_DIRECTUS_URL is required");
}
export const directusBaseUrl = String(rawUrl).trim().replace(/\/$/, "");

/** Optional static token for POST /files when the user is not logged in (public booking). */
function getFileUploadToken() {
  const session = getStoredAccessToken()?.trim();
  if (session) return session;
  return (process.env.REACT_APP_DIRECTUS_FILE_UPLOAD_TOKEN || "").trim();
}

const ACCESS = "directus_access_token";
const REFRESH = "directus_refresh_token";

export function getStoredAccessToken() {
  return localStorage.getItem(ACCESS);
}

export function getStoredRefreshToken() {
  return localStorage.getItem(REFRESH);
}

/** True only if a non-empty access token is stored (local check only). */
export function hasStoredAccessToken() {
  const t = getStoredAccessToken();
  return Boolean(t && String(t).trim());
}

export function setAuthTokens(accessToken, refreshToken) {
  if (accessToken) localStorage.setItem(ACCESS, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH, refreshToken);
}

export function clearAuthTokens() {
  localStorage.removeItem(ACCESS);
  localStorage.removeItem(REFRESH);
}

export function isDirectusConfigured() {
  return Boolean(directusBaseUrl);
}

export const directusApi = axios.create({
  baseURL: directusBaseUrl ? `${directusBaseUrl}/` : undefined,
  headers: { "Content-Type": "application/json" },
});

directusApi.interceptors.request.use((config) => {
  const t = getStoredAccessToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

directusApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._directusRetry &&
      directusBaseUrl
    ) {
      original._directusRetry = true;
      const refresh = localStorage.getItem(REFRESH);
      if (refresh) {
        try {
          const { data } = await axios.post(`${directusBaseUrl}/auth/refresh`, {
            refresh_token: refresh,
          });
          const at = data?.data?.access_token;
          const rt = data?.data?.refresh_token;
          if (at) localStorage.setItem(ACCESS, at);
          if (rt) localStorage.setItem(REFRESH, rt);
          original.headers.Authorization = `Bearer ${at || getStoredAccessToken()}`;
          return directusApi(original);
        } catch {
          clearAuthTokens();
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Confirms the stored token is accepted by Directus. Clears token and cached
 * user on failure. Use this before treating the user as signed in.
 */
export async function validateStoredSession() {
  if (!directusBaseUrl) {
    clearAuthTokens();
    return false;
  }
  if (!hasStoredAccessToken()) {
    clearAuthTokens();
    return false;
  }
  try {
    await directusApi.get("/users/me", { params: { fields: "id" } });
    return true;
  } catch {
    clearAuthTokens();
    try {
      localStorage.removeItem("user");
    } catch {
      // ignore
    }
    return false;
  }
}

function extractUploadedFileId(payload) {
  if (!payload || typeof payload !== "object") return null;
  const d = payload.data;
  if (d && typeof d === "object" && !Array.isArray(d) && d.id) return d.id;
  if (Array.isArray(d) && d[0]?.id) return d[0].id;
  if (payload.id) return payload.id;
  return null;
}

const DIRECTUS_FILE_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isDirectusFileId(value) {
  return typeof value === "string" && DIRECTUS_FILE_ID_RE.test(value.trim());
}

/** Build a URL to view a file in Directus (adds access_token when available). */
export function getDirectusAssetUrl(fileId) {
  if (!directusBaseUrl || !fileId) return "";
  const id = String(fileId).trim();
  if (!id) return "";
  const viewerToken =
    getStoredAccessToken()?.trim() ||
    (process.env.REACT_APP_DIRECTUS_FILE_UPLOAD_TOKEN || "").trim();
  const base = `${directusBaseUrl}/assets/${id}`;
  return viewerToken
    ? `${base}?access_token=${encodeURIComponent(viewerToken)}`
    : base;
}

/**
 * Turn a booking `payment_slip` value (file id, expanded file object, legacy URL/data URL) into an img `src`.
 */
export function resolvePaymentSlipDisplayUrl(paymentSlip) {
  if (paymentSlip == null || paymentSlip === "") return "";
  if (typeof paymentSlip === "object" && paymentSlip.id) {
    return getDirectusAssetUrl(paymentSlip.id);
  }
  if (typeof paymentSlip !== "string") return "";
  const s = paymentSlip.trim();
  if (!s) return "";
  if (
    s.startsWith("data:") ||
    s.startsWith("http://") ||
    s.startsWith("https://")
  ) {
    return s;
  }
  if (isDirectusFileId(s)) return getDirectusAssetUrl(s);
  return s;
}

/**
 * Upload a file to Directus Files. Returns the new `directus_files` row id (UUID).
 * Uses the logged-in user's token, or REACT_APP_DIRECTUS_FILE_UPLOAD_TOKEN for guests.
 */
export async function uploadDirectusFile(file) {
  if (!directusBaseUrl) throw new Error("Directus URL is not configured.");
  if (!file) throw new Error("No file selected.");

  const token = getFileUploadToken();
  if (!token) {
    throw new Error(
      "Missing Directus token for file upload. Sign in or set REACT_APP_DIRECTUS_FILE_UPLOAD_TOKEN for public bookings.",
    );
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const { data } = await axios.post(`${directusBaseUrl}/files`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
    const id = extractUploadedFileId(data);
    if (!id) {
      const hint =
        data?.errors?.[0]?.message ||
        (typeof data === "object"
          ? JSON.stringify(data).slice(0, 280)
          : String(data));
      throw new Error(
        `Directus did not return a file id. Check Files permissions and storage. ${hint}`,
      );
    }
    return id;
  } catch (err) {
    const apiMsg = err?.response?.data?.errors?.[0]?.message;
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      throw new Error(
        apiMsg ||
          "Not allowed to upload files. Grant Create on directus_files for the token's role (and use REACT_APP_DIRECTUS_FILE_UPLOAD_TOKEN for guests if needed).",
      );
    }
    if (apiMsg) throw new Error(apiMsg);
    throw err;
  }
}
